import React, { useState, FormEvent, useEffect } from "react";
import { Container, Form, Button, Card, Row, Col } from "react-bootstrap";
import { CRCIndustries, CRCResult, CRCIndustry } from "./types";
import {
  FaCalculator,
  FaInfoCircle,
  FaCheckCircle,
  FaMinusCircle,
  FaExclamationCircle,
} from "react-icons/fa";
// import './CrcCalculator.css';
import { Box } from "./CrcCalculatorStyle";
import { isInIframe } from "../../utils/is-in-iframe";
import { ToolsHeader } from "../components/ToolsHeader";

const crcIndustries: CRCIndustries = {
  ecommerce: {
    id: "ecommerce",
    name: "E-commerce",
    benchmark: 2.5,
    recommendations: {
      poor: [
        "Optimize your website loading speed to reduce bounce rates",
        "Implement clear and prominent call-to-action buttons",
        "Add customer reviews and social proof",
        "Simplify the checkout process",
        "Offer first-time visitor discounts",
      ],
      moderate: [
        "A/B test different product page layouts",
        "Implement abandoned cart recovery emails",
        "Add product comparison features",
        "Enhance product descriptions and images",
        "Optimize for mobile shopping experience",
      ],
      excellent: [
        "Implement a customer loyalty program",
        "Expand your product range based on analytics",
        "Create personalized shopping experiences",
        "Develop a referral program",
        "Consider international market expansion",
      ],
    },
  },
  lead_generation: {
    id: "lead_generation",
    name: "Lead Generation (B2B & B2C)",
    benchmark: 3.0,
    recommendations: {
      poor: [
        "Simplify your lead capture forms",
        "Create compelling lead magnets",
        "Improve landing page design and clarity",
        "Add trust indicators and testimonials",
        "Implement live chat support",
      ],
      moderate: [
        "A/B test your call-to-action placement",
        "Create targeted content for different segments",
        "Optimize form fields for better completion rates",
        "Add social proof and case studies",
        "Implement exit-intent popups",
      ],
      excellent: [
        "Develop advanced lead scoring system",
        "Create industry-specific landing pages",
        "Implement marketing automation",
        "Develop premium content offerings",
        "Create personalized lead nurturing campaigns",
      ],
    },
  },
  saas: {
    id: "saas",
    name: "SaaS & Subscription Services",
    benchmark: 5.0,
    recommendations: {
      poor: [
        "Offer a free trial period",
        "Simplify the onboarding process",
        "Create clear pricing tiers",
        "Add product demo videos",
        "Implement live chat support",
      ],
      moderate: [
        "Create targeted feature tutorials",
        "Implement user behavior analytics",
        "Optimize your pricing page layout",
        "Add customer success stories",
        "Develop better email onboarding sequence",
      ],
      excellent: [
        "Develop advanced user segmentation",
        "Create enterprise-level features",
        "Implement predictive analytics",
        "Build integration partnerships",
        "Create a customer advisory board",
      ],
    },
  },
  finance: {
    id: "finance",
    name: "Finance & Insurance",
    benchmark: 2.0,
    recommendations: {
      poor: [
        "Add security badges and certifications",
        "Simplify complex financial terms",
        "Create step-by-step application guides",
        "Add FAQ sections for common concerns",
        "Implement trust-building elements",
      ],
      moderate: [
        "Create financial calculators and tools",
        "Develop educational content series",
        "Optimize form completion process",
        "Add video explanations",
        "Implement smart form validation",
      ],
      excellent: [
        "Create personalized financial solutions",
        "Develop premium service offerings",
        "Implement AI-powered recommendations",
        "Create VIP customer programs",
        "Build strategic partnerships",
      ],
    },
  },
  healthcare: {
    id: "healthcare",
    name: "Healthcare & Medical",
    benchmark: 2.8,
    recommendations: {
      poor: [
        "Highlight medical credentials prominently",
        "Add patient testimonials",
        "Simplify appointment booking process",
        "Create clear service descriptions",
        "Implement HIPAA compliance indicators",
      ],
      moderate: [
        "Create health education resources",
        "Optimize patient intake forms",
        "Add virtual consultation options",
        "Implement appointment reminders",
        "Create service comparison tools",
      ],
      excellent: [
        "Develop specialized treatment programs",
        "Create patient loyalty programs",
        "Implement telemedicine solutions",
        "Build health monitoring integrations",
        "Create personalized care plans",
      ],
    },
  },
  education: {
    id: "education",
    name: "Education & E-Learning",
    benchmark: 4.0,
    recommendations: {
      poor: [
        "Offer free sample lessons",
        "Create clear course previews",
        "Simplify enrollment process",
        "Add student success stories",
        "Implement course comparison tools",
      ],
      moderate: [
        "Create targeted learning paths",
        "Implement progress tracking",
        "Add interactive course elements",
        "Create student community features",
        "Develop mobile learning options",
      ],
      excellent: [
        "Create advanced certification programs",
        "Implement AI-powered learning paths",
        "Develop industry partnerships",
        "Create mentor programs",
        "Build career placement services",
      ],
    },
  },
  travel: {
    id: "travel",
    name: "Travel & Hospitality",
    benchmark: 3.2,
    recommendations: {
      poor: [
        "Add high-quality destination photos",
        "Create virtual tours",
        "Implement price guarantees",
        "Add user reviews and ratings",
        "Simplify booking process",
      ],
      moderate: [
        "Create seasonal promotion packages",
        "Implement flexible booking options",
        "Add trip planning tools",
        "Create destination guides",
        "Develop loyalty program",
      ],
      excellent: [
        "Create exclusive travel packages",
        "Implement AI travel recommendations",
        "Develop premium concierge services",
        "Create personalized travel experiences",
        "Build strategic partnerships",
      ],
    },
  },
};

const CRCCalculator: React.FC = () => {
  const [visitors, setVisitors] = useState<number | "">("");
  const [conversions, setConversions] = useState<number | "">("");
  const [industry, setIndustry] = useState<string>("");
  const [result, setResult] = useState<CRCResult | null>(null);
  const [error, setError] = useState<string>("");
  useEffect(() => {
    const height = document.body.scrollHeight;
    window.parent.postMessage({ type: "setHeight", height }, "*");
  }, [result]);
  const calculateConversionRate = (
    visitors: number,
    conversions: number,
    industry: CRCIndustry
  ) => {
    const rate = (conversions / visitors) * 100;

    let status: "poor" | "moderate" | "excellent";
    let message: string;
    if (rate >= industry.benchmark) {
      status = "excellent";
      message =
        "Excellent! Your conversion rate is above the industry benchmark.";
    } else if (rate >= industry.benchmark * 0.7) {
      status = "moderate";
      message =
        "Good! Your conversion rate is close to the industry benchmark.";
    } else {
      status = "poor";
      message = "Poor. Your conversion rate needs improvement.";
    }

    const recommendationKey = status;
    const recommendations = industry.recommendations[recommendationKey];

    return {
      rate,
      status,
      message,
      recommendations,
      benchmark: industry.benchmark,
    };
  };

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError("");
    setResult(null);

    const visitorsValue = Number(visitors);
    const conversionsValue = Number(conversions);
    const selectedIndustry = crcIndustries[industry as keyof CRCIndustries];

    if (!visitorsValue || conversionsValue < 0 || !industry) {
      setError("Please fill in all fields with valid values");
      return;
    }

    if (visitorsValue <= 0) {
      setError("Total Visitors must be greater than 0");
      return;
    }

    const calcResult = calculateConversionRate(
      visitorsValue,
      conversionsValue,
      selectedIndustry
    );
    setResult(calcResult);
    const height = document.body.scrollHeight;
    window.parent.postMessage({ type: "setHeight", height }, "*");
  };

  const handleReset = () => {
    setVisitors("");
    setConversions("");
    setIndustry("");
    setResult(null);
    setError("");
  };

  const renderStatusIcon = (status: "poor" | "moderate" | "excellent") => {
    switch (status) {
      case "excellent":
        return <FaCheckCircle size={32} />;
      case "moderate":
        return <FaMinusCircle size={32} />;
      case "poor":
        return <FaExclamationCircle size={32} />;
      default:
        return null;
    }
  };

  const renderResult = () => {
    if (!result) return null;

    return (
      <Card className="crc-card">
        <Card.Body>
          <div className="crc-result-header">
            <div>
              <h3 className="crc-fs-4">Conversion Rate</h3>
              <p className="crc-rate">{result.rate.toFixed(2)}%</p>
              <p className={`crc-status-message crc-status-${result.status}`}>
                {result.message}
              </p>
            </div>
            <div className={`crc-status-icon crc-status-${result.status}`}>
              {renderStatusIcon(result.status)}
            </div>
          </div>

          <div className="crc-benchmark-section">
            <p className="crc-benchmark">
              Industry Benchmark: <span>{result.benchmark}%</span>
            </p>
            <div className="crc-progress-bar">
              <div
                className={`crc-progress crc-progress-${result.status}`}
                style={{
                  width: `${Math.min(
                    (result.rate / result.benchmark) * 100,
                    100
                  )}%`,
                }}
              />
            </div>
          </div>

          <div className="crc-recommendations-section">
            <h4 className="crc-fs-5">Recommendations</h4>
            <ul className="crc-recommendations-list">
              {result.recommendations.map((rec: string, index: number) => (
                <li key={index}>{rec}</li>
              ))}
            </ul>
          </div>

          <div className="crc-help-text d-flex align-items-center gap-2 p-3 mt-4 rounded crc-bg-light">
            <FaInfoCircle className="text-muted" />
            <span>
              Conversion Rate is calculated using the formula: (Total
              Conversions / Total Visitors) × 100
            </span>
          </div>
        </Card.Body>
      </Card>
    );
  };

  return (
    <Box>
      <Container
        className={`crc-container ${!isInIframe ? "py-5 defaultText" : "py-1 interDisplay"
          }`}
      >
        {!isInIframe && (
          <ToolsHeader
            title="Conversion Rate Calculator"
            description="Calculate Customer Retention Cost (CRC) to improve your business
                strategy."
          />
        )}
        {/* <div className="crc-header text-center mb-5">
          <div className="crc-icon-wrapper d-inline-block mb-3">
            <FaCalculator size={40} className="crc-calculator-icon" />
          </div>
          <h1 className="crc-fs-2">Conversion Rate Calculator</h1>
          <p className="text-muted">Calculate your Conversion Rate with ease</p>
        </div> */}

        <Row className="justify-content-center">
          <Col md={10} lg={8}>
            <div className="crc-main-box">
              <Card className="crc-card mb-4">
                <Card.Body>
                  {error && (
                    <div className="crc-error-message p-3 mb-3 rounded text-danger">
                      {error}
                    </div>
                  )}
                  <Form onSubmit={handleSubmit}>
                    <div className="crc-form-grid">
                      <Form.Group className="crc-form-group">
                        <Form.Label>Total Visitors</Form.Label>
                        <Form.Control
                          type="number"
                          min="1"
                          value={visitors}
                          onChange={(e) =>
                            setVisitors(
                              e.target.value ? Number(e.target.value) : ""
                            )
                          }
                          required
                          className="crc-custom-input"
                        />
                      </Form.Group>

                      <Form.Group className="crc-form-group">
                        <Form.Label>Total Conversions</Form.Label>
                        <Form.Control
                          type="number"
                          min="0"
                          value={conversions}
                          onChange={(e) =>
                            setConversions(
                              e.target.value ? Number(e.target.value) : ""
                            )
                          }
                          required
                          className="crc-custom-input"
                        />
                      </Form.Group>
                    </div>

                    <Form.Group className="crc-form-group mb-4">
                      <Form.Label>Industry / Business Type</Form.Label>
                      <Form.Select
                        value={industry}
                        onChange={(e) => setIndustry(e.target.value)}
                        required
                        className="crc-custom-select"
                      >
                        <option value="">Select an industry...</option>
                        {Object.values(crcIndustries).map(
                          (ind: CRCIndustry) => (
                            <option key={ind.id} value={ind.id}>
                              {ind.name}
                            </option>
                          )
                        )}
                      </Form.Select>
                    </Form.Group>

                    <div className="crc-button-container d-flex gap-3">
                      <Button
                        type="submit"
                        className="flex-grow-1 crc-custom-button d-inline-flex align-items-center justify-content-center"
                      >
                        <FaCalculator className="me-2 crc-btn-icon" />
                        Calculate Conversion Rate
                      </Button>

                      <Button
                        type="button"
                        variant="outline-secondary"
                        className="crc-custom-button crc-secondary-button"
                        onClick={handleReset}
                      >
                        Reset
                      </Button>
                    </div>
                  </Form>
                </Card.Body>
              </Card>

              {renderResult()}
            </div>
          </Col>
        </Row>
      </Container>
    </Box>
  );
};

export default CRCCalculator;
